import React, { useMemo, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faMinus } from '@fortawesome/pro-solid-svg-icons';
import {
  MenuItem,
  SalvBanner,
  SalvBannerAccent,
  SalvBannerType,
  SalvMenuButton,
  SalvTable,
  Size,
} from '@salvapp/ui-components';
import { InternalApiEntityType } from '../../api-internal';
import { useQueryCustomListSchemaFields } from '../schema/customListSchemaActions';
import { useQueryCustomListSignificantFields } from './customListSuiActions';
import Has from '../../auth/Has';
import {
  CAN_MODIFY_CUSTOM_LIST_SIGNIFICANT_FIELD,
  CAN_READ_CUSTOM_LIST_SIGNIFICANT_FIELD,
} from '../../auth/permissions';
import CustomListSignificantFieldsModal from './CustomListSignificantFieldsModal';
import { hasListMissingSui } from './useCustomListSuiWarnings';

interface Props {
  customListId: string;
}

interface CombinedFieldRow {
  fieldId: string;
  fieldName: string;
  isSignificantForPerson: boolean;
  isSignificantForTransaction: boolean;
}

const CustomListSignificantFields = ({ customListId }: Props) => {
  const { data: schemaFieldsData } = useQueryCustomListSchemaFields(customListId);
  const { data: personSignificantFields } = useQueryCustomListSignificantFields(InternalApiEntityType.PERSON);
  const { data: transactionSignificantFields } = useQueryCustomListSignificantFields(InternalApiEntityType.TRANSACTION);

  const [modalEntityType, setModalEntityType] = useState<InternalApiEntityType | null>(null);

  const schemaFields = schemaFieldsData ?? [];
  const parentFields = useMemo(
    () => schemaFields.filter((field) => !field.parentId).sort((a, b) => a.sortOrder - b.sortOrder),
    [schemaFields]
  );

  const personFieldIds = useMemo(() => {
    const config = personSignificantFields?._configuration.find((c) => c.listId === customListId);
    return new Set(config?.fields ?? []);
  }, [personSignificantFields, customListId]);

  const transactionFieldIds = useMemo(() => {
    const config = transactionSignificantFields?._configuration.find((c) => c.listId === customListId);
    return new Set(config?.fields ?? []);
  }, [transactionSignificantFields, customListId]);

  const rows: CombinedFieldRow[] = useMemo(
    () =>
      parentFields.map((field) => ({
        fieldId: field.id,
        fieldName: field.displayName,
        isSignificantForPerson: personFieldIds.has(field.id),
        isSignificantForTransaction: transactionFieldIds.has(field.id),
      })),
    [parentFields, personFieldIds, transactionFieldIds]
  );

  const hasMissingSui = useMemo(() => {
    if (!personSignificantFields || !transactionSignificantFields) {
      return false;
    }
    return hasListMissingSui(customListId, personSignificantFields, transactionSignificantFields);
  }, [personSignificantFields, transactionSignificantFields, customListId]);

  const columnHelper = createColumnHelper<CombinedFieldRow>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('fieldName', { header: 'Schema Field' }),
      columnHelper.accessor('isSignificantForPerson', {
        header: () => <div style={{ textAlign: 'center' }}>Person</div>,
        cell: ({ row }) => (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {row.original.isSignificantForPerson ? (
              <FontAwesomeIcon icon={faCheck} style={{ color: 'var(--success-default)' }} />
            ) : (
              <FontAwesomeIcon icon={faMinus} style={{ color: 'var(--secondary-default)' }} />
            )}
          </div>
        ),
      }),
      columnHelper.accessor('isSignificantForTransaction', {
        header: () => <div style={{ textAlign: 'center' }}>Transaction</div>,
        cell: ({ row }) => (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {row.original.isSignificantForTransaction ? (
              <FontAwesomeIcon icon={faCheck} style={{ color: 'var(--success-default)' }} />
            ) : (
              <FontAwesomeIcon icon={faMinus} style={{ color: 'var(--secondary-default)' }} />
            )}
          </div>
        ),
      }),
    ],
    []
  );

  const significantFieldIdsForModal = useMemo(() => {
    if (modalEntityType === InternalApiEntityType.PERSON) {
      return Array.from(personFieldIds);
    }
    if (modalEntityType === InternalApiEntityType.TRANSACTION) {
      return Array.from(transactionFieldIds);
    }
    return [];
  }, [modalEntityType, personFieldIds, transactionFieldIds]);

  return (
    <Has allPermissions={CAN_READ_CUSTOM_LIST_SIGNIFICANT_FIELD}>
      {hasMissingSui && (
        <SalvBanner
          message='Significant Update Indicators are not configured for this list. Changes to list records will not trigger re-screening.'
          type={SalvBannerType.WARNING}
          accent={SalvBannerAccent.STRONG}
          className='m-b-16'
        />
      )}
      <div className='m-b-24'>
        <SalvTable
          title='Significant Update Indicators'
          columns={columns}
          data={rows}
          emptyMessage='No schema fields configured'
          actions={
            <Has allPermissions={CAN_MODIFY_CUSTOM_LIST_SIGNIFICANT_FIELD}>
              <SalvMenuButton
                label='Edit'
                size={Size.SMALL}
                placement='bottom end'
                onAction={([action]) => {
                  if (action === 'person') {
                    setModalEntityType(InternalApiEntityType.PERSON);
                  } else if (action === 'transaction') {
                    setModalEntityType(InternalApiEntityType.TRANSACTION);
                  }
                }}
              >
                <MenuItem title='Person' value='person' />
                <MenuItem title='Transaction' value='transaction' />
              </SalvMenuButton>
            </Has>
          }
        />
        {modalEntityType && (
          <CustomListSignificantFieldsModal
            listId={customListId}
            entityType={modalEntityType}
            schemaFields={schemaFields}
            significantFieldIds={significantFieldIdsForModal}
            onClose={() => setModalEntityType(null)}
            show={true}
          />
        )}
      </div>
    </Has>
  );
};

export default CustomListSignificantFields;
